import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Card } from "react-bootstrap";
import { checkRegistrationExpiry } from "../../utils/RegistrationExpiryHelper";

const Home = () => {
  const [sessionData, setSessionData] = useState(() => {
    try {
      return {
        firm: JSON.parse(localStorage.getItem("selectedFirm") || "null"),
        branch: JSON.parse(localStorage.getItem("selectedBranch") || "null"),
        month: localStorage.getItem("selectedPayrollMonth") || "",
        year: localStorage.getItem("selectedPayrollYear") || "",
      };
    } catch {
      return { firm: null, branch: null, month: "", year: "" };
    }
  });

  const userData = (() => {
    try {
      return JSON.parse(localStorage.getItem("authUser") || "{}");
    } catch {
      return {};
    }
  })();

  const syncSession = useCallback(() => {
    try {
      setSessionData({
        firm: JSON.parse(localStorage.getItem("selectedFirm") || "null"),
        branch: JSON.parse(localStorage.getItem("selectedBranch") || "null"),
        month: localStorage.getItem("selectedPayrollMonth") || "",
        year: localStorage.getItem("selectedPayrollYear") || "",
      });
    } catch {
      setSessionData({ firm: null, branch: null, month: "", year: "" });
    }
  }, []);

  useEffect(() => {
    window.addEventListener("storage", syncSession);
    return () => window.removeEventListener("storage", syncSession);
  }, [syncSession]);

  const isSessionReady = !!(sessionData.firm && sessionData.month && sessionData.year);
  const expiryAlert = checkRegistrationExpiry(sessionData.branch);

  const quickLinks = [
    {
      title: "Single Payroll Entry",
      desc: "Individual employee attendance, earnings, deductions & net pay processing.",
      icon: "fa fa-calculator",
      url: "/Payroll",
      color: "#4f46e5",
      bgLight: "rgba(79, 70, 229, 0.08)",
      borderColor: "rgba(79, 70, 229, 0.2)",
    },
    {
      title: "Multi Payroll Entry",
      desc: "Grid-based batch payroll calculations and fast bulk data entry.",
      icon: "fa fa-table",
      url: "/MultiPayrollEntry",
      color: "#0891b2",
      bgLight: "rgba(8, 145, 178, 0.08)",
      borderColor: "rgba(8, 145, 178, 0.2)",
    },
    {
      title: "Wages Register",
      desc: "Department-wise wages register, legal registers & payment summaries.",
      icon: "fa fa-file-text-o",
      url: "/WagesRegisterDeptWise",
      color: "#059669",
      bgLight: "rgba(5, 150, 105, 0.08)",
      borderColor: "rgba(5, 150, 105, 0.2)",
    },
    {
      title: "Wages Slip (Individual)",
      desc: "Generate and print detailed individual monthly salary slips with breakdown.",
      icon: "fa fa-id-card-o",
      url: "/WagesSlipIndividual",
      color: "#d97706",
      bgLight: "rgba(217, 119, 6, 0.08)",
      borderColor: "rgba(217, 119, 6, 0.2)",
    },
    {
      title: "PF & ESI Compliance",
      desc: "PF Challan reports, Form 11, Form 12 & ESI monthly deduction returns.",
      icon: "fa fa-shield",
      url: "/PfChallanReport",
      color: "#e11d48",
      bgLight: "rgba(225, 29, 72, 0.08)",
      borderColor: "rgba(225, 29, 72, 0.2)",
    },
    {
      title: "Employee Master",
      desc: "Manage workforce records, salary structures, bank details & statutory numbers.",
      icon: "fa fa-users",
      url: "/EmployeeMaster",
      color: "#7c3aed",
      bgLight: "rgba(124, 58, 237, 0.08)",
      borderColor: "rgba(124, 58, 237, 0.2)",
    },
  ];

  return (
    <div className="container-fluid" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* Welcome Hero Banner */}
      <div
        className="mb-4 p-4 p-md-5 text-white position-relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #1e3c72 100%)",
          borderRadius: "16px",
          boxShadow: "0 10px 25px -5px rgba(30, 60, 114, 0.3)",
        }}
      >
        <div style={{ position: "relative", zIndex: 2 }}>
          <div className="d-flex align-items-center mb-2">
            <span
              className="badge px-3 py-1 me-2"
              style={{
                background: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(4px)",
                fontSize: "12px",
                borderRadius: "20px",
                fontWeight: 500,
              }}
            >
              <i className="fa fa-sparkles me-1" />
              Payroll System
            </span>
            <span className="text-white-50 fs-13">
              {new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            </span>
          </div>

          <h2 className="fw-bold mb-2 text-white" style={{ fontSize: "1.75rem" }}>
            Welcome{userData?.name ? `, ${userData.name}` : ""}! 👋
          </h2>
          <p className="mb-0 text-white-50" style={{ maxWidth: "680px", fontSize: "14px", lineHeight: "1.6" }}>
            Welcome to the Payroll Management Portal. Manage wage records, statutory reports, employee attendance, and compliance efficiently.
          </p>
        </div>

        {/* Decorative Background Circles */}
        <div
          style={{
            position: "absolute",
            right: "-40px",
            bottom: "-40px",
            width: "220px",
            height: "220px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.06)",
            zIndex: 1,
          }}
        />
        <div
          style={{
            position: "absolute",
            right: "120px",
            top: "-30px",
            width: "140px",
            height: "140px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.04)",
            zIndex: 1,
          }}
        />
      </div>

      {/* Registration Expiry Urgent Alert Banner (if branch registration is within 6 months or expired) */}
      {expiryAlert && (
        <div
          className="mb-4 p-4 border rounded shadow-sm position-relative overflow-hidden"
          style={{
            background: expiryAlert.isExpired
              ? "linear-gradient(135deg, #fff5f5 0%, #fee2e2 100%)"
              : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
            borderColor: expiryAlert.isExpired ? "#fca5a5" : "#fde047",
            borderRadius: "14px",
            borderLeft: `6px solid ${expiryAlert.isExpired ? '#dc2626' : '#d97706'}`,
            boxShadow: expiryAlert.isExpired
              ? "0 4px 12px rgba(220, 38, 38, 0.12)"
              : "0 4px 12px rgba(217, 119, 6, 0.12)",
          }}
        >
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
            <div className="d-flex align-items-start">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                style={{
                  width: "50px",
                  height: "50px",
                  background: expiryAlert.isExpired ? "#fee2e2" : "#fef3c7",
                  color: expiryAlert.isExpired ? "#dc2626" : "#d97706",
                  fontSize: "24px",
                  border: `2px solid ${expiryAlert.isExpired ? '#fca5a5' : '#fcd34d'}`,
                }}
              >
                <i className={`fa ${expiryAlert.isExpired ? 'fa-exclamation-triangle' : 'fa-clock-o'}`} />
              </div>
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h5 className="mb-0 fw-bold fs-16" style={{ color: expiryAlert.isExpired ? '#991b1b' : '#92400e' }}>
                    {expiryAlert.isExpired
                      ? "⚠️ Branch Registration Has Expired!"
                      : `⚠️ Branch Registration Expiring in ${expiryAlert.diffDays} Days!`}
                  </h5>
                  <span
                    className={`badge ${expiryAlert.isExpired ? 'bg-danger' : 'bg-warning text-dark'} px-2 py-1`}
                    style={{ fontSize: "11px", fontWeight: 600 }}
                  >
                    {expiryAlert.isExpired ? "EXPIRED" : "RENEWAL DUE"}
                  </span>
                </div>
                <p className="mb-1 fs-13" style={{ color: expiryAlert.isExpired ? '#b91c1c' : '#b45309' }}>
                  Branch <strong>"{expiryAlert.branchName}"</strong> {expiryAlert.fbRegNo ? <>(Reg No: <strong>{expiryAlert.fbRegNo}</strong>)</> : null} registration validity {expiryAlert.isExpired ? `expired on ${expiryAlert.formattedDate} (${expiryAlert.diffDays} days ago)` : `will expire on ${expiryAlert.formattedDate} (${expiryAlert.diffDays} days remaining)`}.
                </p>
                <small className="text-muted fs-12">
                  <i className="fa fa-info-circle me-1" />
                  Please complete the factory / shop & establishment license renewal process on time to avoid any compliance issues.
                </small>
              </div>
            </div>

            <div className="flex-shrink-0">
              <Link
                to="/FirmMaster"
                className={`btn btn-sm px-3 py-2 ${expiryAlert.isExpired ? 'btn-danger' : 'btn-warning text-dark'}`}
                style={{ borderRadius: "8px", fontWeight: 600, fontSize: "12.5px" }}
              >
                <i className="fa fa-pencil-square-o me-1" /> Update Firm / Branch
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Header Selection Notice & Session Status Card */}
      <div className="mb-4">
        {isSessionReady ? (
          <div
            className="p-4 border rounded shadow-sm"
            style={{
              background: "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
              borderColor: "#86efac",
              borderRadius: "14px",
              boxShadow: "0 4px 6px -1px rgba(16, 185, 129, 0.1)",
            }}
          >
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
              <div className="d-flex align-items-center">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                  style={{
                    width: "48px",
                    height: "48px",
                    background: "#dcfce7",
                    color: "#16a34a",
                    fontSize: "20px",
                  }}
                >
                  <i className="fa fa-check-circle" />
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h5 className="mb-0 fw-bold text-dark fs-16">Active Payroll Session</h5>
                    <span className="badge bg-success px-2 py-1" style={{ fontSize: "11px" }}>
                      Ready
                    </span>
                  </div>
                  <div className="text-muted fs-13">
                    Active Firm: <strong className="text-dark">{sessionData.firm.Name}</strong>
                    {sessionData.branch && (
                      <>
                        {" "}| Branch: <strong className="text-dark">{sessionData.branch.Name}</strong>
                      </>
                    )}
                    {" "}| Wage Period: <strong className="text-primary">{sessionData.month} {sessionData.year}</strong>
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2">
                <span className="text-muted fs-12 d-none d-lg-inline">
                  <i className="fa fa-info-circle me-1" />
                  To switch firm or period, use the dropdowns in the <strong>top header</strong>.
                </span>
                <Link to="/Payroll" className="btn btn-primary btn-sm px-3" style={{ borderRadius: "8px" }}>
                  <i className="fa fa-arrow-right me-1" /> Open Payroll
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="p-4 border rounded shadow-sm position-relative"
            style={{
              background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
              borderColor: "#fcd34d",
              borderRadius: "14px",
              boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.1)",
            }}
          >
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
              <div className="d-flex align-items-start align-items-md-center">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                  style={{
                    width: "48px",
                    height: "48px",
                    background: "#fde68a",
                    color: "#d97706",
                    fontSize: "18px"
                  }}
                >
                  <i className="fa fa-info-circle" />
                </div>
                <div>
                  <h5 className="mb-1 fw-bold text-dark fs-16" style={{ color: "#92400e" }}>
                    Please select Firm, Month & Year from Header
                  </h5>
                  <p className="mb-0 fs-13" style={{ color: "#b45309" }}>
                    Please select <strong>Active Firm</strong>, <strong>Branch</strong>, <strong>Wage Month</strong> and <strong>Wage Year</strong> from the header navbar to calculate payroll and generate reports.
                  </p>
                </div>
              </div>

              <div className="flex-shrink-0">
                <span
                  className="badge px-3 py-2 d-inline-flex align-items-center"
                  style={{
                    background: "#d97706",
                    color: "#ffffff",
                    borderRadius: "8px",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  <i className="fa fa-arrow-up me-2" />
                  Select From Top Navbar
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Navigation / Features Grid */}
      <div className="mb-3">
        <h5 className="fw-bold text-dark mb-3 fs-15">
          <i className="fa fa-th-large text-primary me-2" />
          Quick Access Modules
        </h5>

        <Row className="g-3">
          {quickLinks.map((item, idx) => (
            <Col key={idx} xl={4} md={6} sm={12}>
              <Link to={item.url} className="text-decoration-none">
                <Card
                  className="h-100 border-0 shadow-sm transition-all"
                  style={{
                    borderRadius: "12px",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    cursor: "pointer",
                    border: `1px solid ${item.borderColor}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 10px 20px rgba(0,0,0,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
                  }}
                >
                  <Card.Body className="p-3 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                      style={{
                        width: "50px",
                        height: "50px",
                        background: item.bgLight,
                        color: item.color,
                        fontSize: "22px",
                        borderRadius: "10px",
                      }}
                    >
                      <i className={item.icon} />
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="fw-bold text-dark mb-1 fs-14">{item.title}</h6>
                      <p className="text-muted mb-0 fs-12" style={{ lineHeight: "1.4" }}>
                        {item.desc}
                      </p>
                    </div>
                    <i className="fa fa-chevron-right text-muted ms-2 fs-12" />
                  </Card.Body>
                </Card>
              </Link>
            </Col>
          ))}
        </Row>
      </div>

      {/* System Guidelines / Info Footer Card */}
      <Row className="mt-4">
        <Col xl={12}>
          <div
            className="p-3 bg-light border rounded d-flex flex-column flex-md-row align-items-md-center justify-content-between text-muted fs-12"
            style={{ borderRadius: "10px" }}
          >
            <div className="d-flex align-items-center mb-2 mb-md-0">
              <i className="fa fa-info-circle text-primary me-2 fs-14" />
              <span>
                <strong>Session Persistence:</strong> Selected Firm, Branch, Month, and Year are saved automatically in your session and shared across all payroll modules and reports.
              </span>
            </div>
            <div className="d-flex align-items-center gap-3">
              <span>
                <i className="fa fa-keyboard-o me-1" />
                Press <kbd className="bg-white text-dark border px-1">Alt + K</kbd> for shortcuts
              </span>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Home;